import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Info,
  RefreshCw,
  Search
} from 'lucide-react';
import { ResponsiveTooltip } from '@/components/ui/responsive-tooltip';
import { Button } from '@/components/ui/button';

// ============================================
// TYPES
// ============================================
export interface EvidenceBoxProps {
  queryUsed: string;
  rawResponseSnippet: string;
  brandName: string;
  queryType: 'discovery' | 'fallback' | 'not_found' | 'error';
  aiScore?: number;
  competitorMentions?: string[];
  error?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
}

// ============================================
// STATUS CONFIGURATION
// ============================================
const statusConfig = {
  discovery: {
    icon: CheckCircle2,
    iconColor: 'text-emerald-500',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    label: 'Mentioned in AI recommendations',
    labelMobile: 'AI Recommended',
    description: 'Great! AI actively recommends your brand when users search for this topic.',
    badgeVariant: 'default' as const,
  },
  fallback: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    borderColor: 'border-amber-200 dark:border-amber-800',
    label: 'Recognized but not recommended',
    labelMobile: 'Recognized Only',
    description: "AI knows your brand exists, but doesn't recommend it for this search. GEO optimization needed.",
    badgeVariant: 'secondary' as const,
  },
  not_found: {
    icon: XCircle,
    iconColor: 'text-rose-500',
    bgColor: 'bg-rose-50 dark:bg-rose-950/30',
    borderColor: 'border-rose-200 dark:border-rose-800',
    label: 'Not found in AI responses',
    labelMobile: 'Not Found',
    description: "AI doesn't mention your brand for this topic. Significant SEO/GEO work needed.",
    badgeVariant: 'destructive' as const,
  },
  error: {
    icon: AlertTriangle,
    iconColor: 'text-muted-foreground',
    bgColor: 'bg-muted/50',
    borderColor: 'border-border',
    label: 'Unable to complete scan',
    labelMobile: 'Scan Failed',
    description: 'We encountered an issue querying the AI. This may be temporary.',
    badgeVariant: 'outline' as const,
  }
};

// ============================================
// COMPONENT
// ============================================
export function EvidenceBox({
  queryUsed,
  rawResponseSnippet,
  brandName,
  queryType,
  aiScore,
  competitorMentions,
  error = false,
  errorMessage,
  onRetry
}: EvidenceBoxProps) {
  const status = statusConfig[queryType] || statusConfig.error;
  const StatusIcon = status.icon;

  // Error state UI
  if (queryType === 'error' || error) {
    return (
      <Card className="border-border bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base font-semibold text-foreground">
                Live Verification Log
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`rounded-lg p-4 ${status.bgColor} ${status.borderColor} border`}>
            <StatusIcon className={`h-8 w-8 ${status.iconColor} mx-auto mb-3`} />
            <p className="text-center font-medium text-foreground mb-2">
              Unable to complete AI scan
            </p>
            <p className="text-center text-sm text-muted-foreground mb-4">
              {errorMessage || 'We encountered an issue querying ChatGPT. This may be due to high traffic or temporary API limits.'}
            </p>
            {onRetry && (
              <Button 
                onClick={onRetry} 
                variant="outline" 
                size="sm" 
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Scan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Normal state UI
  return (
    <Card className="border-border bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Search className="h-5 w-5 text-primary flex-shrink-0" />
            <CardTitle className="text-base font-semibold text-foreground truncate">
              Live Verification Log
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Query Section */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">We asked ChatGPT:</p>
          <div className="rounded-lg bg-muted/50 p-3 border border-border">
            <p className="font-mono text-sm text-foreground break-words whitespace-pre-wrap">
              "{queryUsed}"
            </p>
          </div>
        </div>

        {/* Response Section */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">AI responded:</p>
          <div className="rounded-lg bg-muted/50 p-3 border border-border max-h-32 overflow-y-auto">
            <p className="font-mono text-xs text-muted-foreground break-words whitespace-pre-wrap leading-relaxed">
              "{rawResponseSnippet
                ?.replace(/(\*\*|__)(.*?)\1/g, '$2')
                .replace(/^#{1,6}\s+/gm, '')
                .replace(/[*`_]/g, '')
              }"
            </p>
          </div>
        </div>

        {/* Status Section */}
        <div className={`rounded-lg p-4 sm:p-4 ${status.bgColor} ${status.borderColor} border`}>
          <div className="flex items-start gap-3">
            <StatusIcon className={`h-5 w-5 ${status.iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-2 mb-1">
                <span className="text-sm text-muted-foreground">
                  Your brand ({brandName}):
                </span>
                <Badge variant={status.badgeVariant} className="text-xs w-fit whitespace-nowrap">
                  <span className="hidden sm:inline">{status.label}</span>
                  <span className="sm:hidden">{status.labelMobile}</span>
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {status.description}
              </p>
            </div>
          </div>
        </div>

        {/* Competitor Mentions */}
        {(aiScore === 0 || aiScore === 20) && competitorMentions && competitorMentions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm font-semibold text-foreground mb-2.5">
              {aiScore === 0
                ? "Brands AI recommends instead of you:"
                : "Brands AI recommends over you:"}
            </p>
            <div className="flex flex-wrap gap-2">
              {competitorMentions.map((brand, idx) => (
                <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 shadow-sm">
                  {brand}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Based on ChatGPT's response for "{queryUsed}"
            </p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="flex items-start gap-2 pt-2 border-t border-border">
          <ResponsiveTooltip
            content="AI responses are non-deterministic and may vary between queries. This scan represents a point-in-time snapshot of AI visibility."
          >
            <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-help" />
          </ResponsiveTooltip>
          <p className="text-xs text-muted-foreground">
            AI responses vary. This is a point-in-time snapshot.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default EvidenceBox;
